/**
 * Score dos Anúncios — redesenho de 21/09/2026 (direções da Mariana, ao vivo na prévia):
 *
 *  - O painel é o MAPA: KPIs, fila "O que fazer hoje" e gráficos sempre sobre a base inteira.
 *    Filtro nunca recalcula o painel — era o que confundia.
 *  - TUDO no painel é clicável e filtra a lista "Todos os anúncios" lá embaixo. Cada card da fila
 *    tem o botão "Ver os N anúncios".
 *  - Os filtros ficam COLADOS no quadro "Todos os anúncios", e os filtros ativos aparecem em chips
 *    (cada um com ×, mais "Limpar tudo").
 *  - Na lista, a coluna Pendências mostra as tags principais e o resto no hover.
 *  - Aba "Preço × concorrência" (AbaConcorrencia) com a comparação de preço do ML.
 *  - Saiu da UI (pedido da Mariana): índice interno Fiesta + 5 pilares.
 *  - Crivo Fiestaup: a "Leitura da coleta" (LLM) permanece — tela com dados = PropostaIa.
 *
 * Regras e cálculo em ./scoreAnuncios/logic.ts — nada é recalculado aqui.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, LinearProgress, Link, MenuItem, Stack, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material'
import { AbaConcorrencia } from './scoreAnuncios/AbaConcorrencia'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ChartTip, XAxis, YAxis } from 'recharts'
import { ChartEmpty } from '../components/ChartEmpty'
import { DomainPageShell } from '../components/DomainPageShell'
import { KpiCard } from '../components/KpiCard'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { fmtBrl } from '../api/bridge'
import { dispararColetaScore, fetchScoreAnalise, fetchScoreDados, fetchScoreStatus } from './scoreAnuncios/scoreService'
import {
  ADS_LABEL, EXPL, FAIXAS, FILTROS_VAZIOS, FORA_DA_FILA, GAUGE_LABEL, NIVEL_LABEL, NOME_CONTA, RULE_PT,
  aplicaFiltros, buildRows, computeRecs, computeRegrasPendentes, contaFiltrosAtivos, descreveFiltros,
  faixaKey, janelaPadrao, janelasDisponiveis, opcoesAds, opcoesExposicao, rotuloFaixa, sum,
  type Filtros, type RawData, type Row,
} from './scoreAnuncios/logic'

const nf = (n: number | null | undefined, d = 0): string =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : n.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d })
const pctFmt = (n: number | null | undefined, d = 1): string => (n === null || n === undefined || Number.isNaN(n) ? '—' : `${nf(n, d)}%`)
const dia = (s?: string) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '—')

const PST_LABEL: Record<string, [string, string]> = {
  with_benchmark_highest: ['error.main', 'acima do mercado'],
  with_benchmark_high: ['warning.main', 'um pouco acima'],
  no_benchmark_lowest: ['success.main', 'menor preço'],
  no_benchmark_ok: ['success.main', 'dentro da faixa'],
}
const PAGE_SIZE = 60
type SortKey = 'r' | 'sc' | 'vi' | 'u' | 'conv' | 'np' | 'ac' | 'ro'
type Aba = 'score' | 'preco'

function Secao({ titulo, sub, children, action }: { titulo: string; sub?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: sub ? 0.25 : 1.5 }}>
          <Typography variant="h6" sx={{ mr: 'auto', fontSize: 16 }}>{titulo}</Typography>
          {action}
        </Stack>
        {sub ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{sub}</Typography> : null}
        {children}
      </CardContent>
    </Card>
  )
}

/** Bloco de filtros com título numerado. Fica fora do render: definido dentro, o campo Buscar perderia o foco a cada tecla. */
function Bloco({ titulo, children, md }: { titulo: string; children: ReactNode; md: number }) {
  return (
    <Grid size={{ xs: 12, md }}>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.25, height: '100%' }}>
        <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', mb: 1 }}>{titulo}</Typography>
        <Stack spacing={1}>{children}</Stack>
      </Box>
    </Grid>
  )
}

/** Linha clicável de "barra + rótulo + valor" usada em experiência, estrelas e regras. */
function Barra({ label, valor, pct, cor, sub, ativo, onClick, altura = 8 }: { label: ReactNode; valor: ReactNode; pct: number; cor: string; sub?: ReactNode; ativo?: boolean; onClick?: () => void; altura?: number }) {
  const t = useTheme()
  return (
    <Box onClick={onClick} sx={{ mb: 0.9, px: 0.75, py: 0.4, mx: -0.75, borderRadius: 1.5, cursor: onClick ? 'pointer' : undefined, outline: ativo ? `2px solid ${t.palette.primary.main}` : undefined, '&:hover': onClick ? { bgcolor: alpha(t.palette.primary.main, 0.06) } : undefined }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="body2" sx={{ fontSize: 13 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: 13 }}>{valor}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, pct))} sx={{ mt: 0.4, height: altura, borderRadius: 999, bgcolor: alpha(t.palette.text.primary, 0.08), '& .MuiLinearProgress-bar': { bgcolor: cor, borderRadius: 999 } }} />
      {sub ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>{sub}</Typography> : null}
    </Box>
  )
}

/** Coleta incompleta: erros de API gravados pelo coletor no meta (pedido da Mariana, 24/09/2026). */
function AvisoColeta({ erros }: { erros: NonNullable<RawData['meta']['erros']> }) {
  const codigos = Object.entries(erros.por_codigo || {})
  const partes: string[] = [`${nf(erros.total)} chamadas à API do Mercado Livre falharam nesta extração`]
  if (codigos.length) partes.push(`(${codigos.map(([c, n]) => `${c}: ${n}`).join(', ')})`)
  if (erros.sem_visitas) partes.push(`· ${nf(erros.sem_visitas)} anúncios sem visitas lidas`)
  const dicas: string[] = []
  if (erros.por_codigo?.['401']) dicas.push('Código 401 = token do ML recusado: renovar o OAuth no S1 e coletar de novo.')
  if (erros.por_codigo?.['403']) dicas.push('Código 403 = a conta não tem permissão para esse recurso.')
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <b>Coleta incompleta:</b> {partes.join(' ')}. Unidades, visitas, ACOS e ROAS podem estar zerados nesses anúncios. {dicas.join(' ')}
      {erros.amostra?.length ? <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: 11, opacity: 0.8 }}>Amostra: {erros.amostra.slice(0, 5).join(' · ')}</Box> : null}
    </Alert>
  )
}

export function ScoreAnunciosPage() {
  const theme = useTheme()
  const chart = theme.chart
  const [raw, setRaw] = useState<RawData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [coletando, setColetando] = useState(false)
  const [coletaMsg, setColetaMsg] = useState<string | null>(null)
  const [coletaErro, setColetaErro] = useState<string | null>(null)
  const [visivel, setVisivel] = useState(PAGE_SIZE)
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VAZIOS)
  const [sort, setSort] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: 'r', dir: -1 })
  const [aba, setAba] = useState<Aba>('score')
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })
  const tabelaRef = useRef<HTMLDivElement | null>(null)

  const setF = <K extends keyof Filtros>(k: K, v: Filtros[K]) => setFiltros((f) => ({ ...f, [k]: v }))
  /** Clique no painel: aplica o filtro (ou desliga, se já era esse) e leva até a lista. */
  const filtrar = <K extends keyof Filtros>(k: K, v: Filtros[K]) => {
    setFiltros((f) => ({ ...f, [k]: f[k] === v ? FILTROS_VAZIOS[k] : v }))
    window.setTimeout(() => tabelaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
  }
  const limpar = () => setFiltros(FILTROS_VAZIOS)

  const loadAnalise = () => {
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    fetchScoreAnalise()
      .then((data) => {
        if (!data.ok) {
          setLeituraIa({
            loading: false,
            texto: null,
            fonte: 'regras',
            modelo: null,
            erro: data.erro || data.message || data.error || 'Análise indisponível',
          })
          return
        }
        setLeituraIa({
          loading: false,
          texto: data.texto || null,
          modelo: data.modelo,
          fonte: data.fonte,
          cached: data.cached,
          gerado_em: data.gerado_em,
        })
      })
      .catch((err: unknown) => {
        setLeituraIa({
          loading: false,
          texto: null,
          fonte: 'regras',
          modelo: null,
          erro: err instanceof Error ? err.message : 'Falha ao carregar a análise',
        })
      })
  }

  // leitura vem pronta do serviço (/score/analise): foco personalizado ainda não se aplica
  useRegisterPanelInsights({
    titulo: 'Score',
    verdade: 'Verdade = números da última coleta da API do ML (tabela desta tela). Proposta = leitura da IA — revise antes de agir.',
    estado: leituraIa,
    recarregar: loadAnalise,
    aceitaFoco: false,
  })

  const load = () => {
    setLoading(true); setError(null)
    fetchScoreDados<RawData>()
      .then((data) => {
        setRaw(data)
        loadAnalise()
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Falha ao carregar o painel de score'))
      .finally(() => setLoading(false))
  }
  const dispararColeta = async () => {
    setColetaErro(null); setColetaMsg(null); setColetando(true)
    try {
      const r = await dispararColetaScore()
      setColetaMsg(r.mensagem || 'Coleta disparada — costuma levar 25–35 min.')
    } catch (err: unknown) {
      setColetando(false)
      setColetaErro(err instanceof Error ? err.message : 'Falha ao disparar a coleta')
    }
  }
  useEffect(() => { load() }, [])

  // acompanha a coleta enquanto houver uma rodando
  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    const poll = async () => {
      try {
        const st = await fetchScoreStatus()
        if (cancelled) return
        if (st.rodando) {
          setColetando(true)
          setColetaMsg((m) => m || 'Coleta em curso (~25–35 min).')
          timer = window.setTimeout(poll, 15_000)
          return
        }
        if (coletando) {
          setColetando(false)
          setColetaMsg('Coleta concluída — atualizando o painel…')
          load()
          window.setTimeout(() => { if (!cancelled) setColetaMsg(null) }, 4000)
        }
      } catch {
        if (!cancelled && coletando) timer = window.setTimeout(poll, 20_000)
      }
    }
    void poll()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [coletando])

  const janelasOpt = useMemo(() => janelasDisponiveis(raw), [raw])
  const [janela, setJanela] = useState('')
  useEffect(() => { setJanela(janelaPadrao(raw)) }, [raw])

  /** A base inteira no período. O painel mede sempre isto. */
  const rowsAll = useMemo(() => (raw ? buildRows(raw, janela || undefined) : []), [raw, janela])
  /** Só a lista "Todos os anúncios" obedece aos filtros. */
  const rowsLista = useMemo(() => aplicaFiltros(rowsAll, filtros), [rowsAll, filtros])
  const nFiltros = contaFiltrosAtivos(filtros)
  const meta = raw?.meta
  const w = (janela ? meta?.windows?.[janela] : undefined) ?? meta?.window
  const diasJanela = w?.visits_days ?? (janela ? Number(janela) : 30)
  const chipsFiltro = useMemo(() => descreveFiltros(filtros, diasJanela), [filtros, diasJanela])

  const optExposicao = useMemo(() => opcoesExposicao(rowsAll), [rowsAll])
  const optAds = useMemo(() => opcoesAds(rowsAll), [rowsAll])
  const optRegras = useMemo(() => computeRegrasPendentes(rowsAll), [rowsAll])

  // ---- números do painel (base inteira) ----
  const rows = rowsAll
  const comScore = useMemo(() => rows.filter((r) => typeof r.sc === 'number'), [rows])
  const scoreMedio = comScore.length ? sum(comScore, (r) => r.sc) / comScore.length : 0
  const nGood = rows.filter((r) => r.lv === 'good').length
  const visT = sum(rows, (r) => r.vi), unT = sum(rows, (r) => r.u), recT = sum(rows, (r) => r.r)
  const clT = sum(rows, (r) => r.cl), prT = sum(rows, (r) => r.pr), custoT = sum(rows, (r) => r.adc)
  const pedT = sum(rows, (r) => r.pe), reclT = sum(rows, (r) => r.rc), cancT = sum(rows, (r) => r.cn), avT = sum(rows, (r) => r.nav)
  const estrelas = sum(rows, (r) => (r.rv ? r.rv.l.reduce((s, x, i) => s + x * (i + 1), 0) : 0))
  const recs = useMemo(() => computeRecs(rows), [rows])
  const regrasPendentes = useMemo(() => computeRegrasPendentes(rows).slice(0, 12), [rows])
  const grupos = useMemo(() => FAIXAS.map((f) => ({ f, rows: comScore.filter((r) => (r.sc || 0) >= f[0] && (r.sc || 0) < f[1]) })), [comScore])
  const semScore = rows.length - comScore.length
  const distScoreData = useMemo(() => [
    ...grupos.map((g) => ({ name: rotuloFaixa(g.f), key: faixaKey(g.f), total: g.rows.length, boa: g.f[0] >= 75 })),
    { name: 'sem score', key: 'sem', total: semScore, boa: false },
  ], [grupos, semScore])
  const convFaixaData = useMemo(() => grupos.map((g) => {
    const vi = sum(g.rows, (r) => r.vi), u = sum(g.rows, (r) => r.u)
    return { name: rotuloFaixa(g.f), key: faixaKey(g.f), conv: vi ? (u / vi) * 100 : 0, anuncios: g.rows.length }
  }), [grupos])
  const gzCounts = useMemo(() => ([
    { k: 'healthy', l: 'Saudável', color: theme.palette.success.main },
    { k: 'warning', l: 'Atenção', color: theme.palette.warning.main },
    { k: 'unhealthy', l: 'Com problema', color: theme.palette.error.main },
    { k: 'sem', l: 'Sem histórico', color: theme.palette.action.disabled },
  ]).map((g) => ({ ...g, v: rows.filter((r) => (r.gz || 'sem') === g.k).length })), [rows, theme])
  const estrelasDist = useMemo(() => { const e = [0, 0, 0, 0, 0]; rows.forEach((r) => { if (r.rv) r.rv.l.forEach((n, i) => (e[i] += n)) }); return e }, [rows])
  const estrelasTot = estrelasDist.reduce((a, b) => a + b, 0)
  const estrelasMax = Math.max(...estrelasDist, 1)
  const baixaExpRows = useMemo(() => [...rows].filter((r) => r.exp.length > 0).sort((a, b) => (b.r || 0) - (a.r || 0)).slice(0, 10), [rows])

  // ---- lista ----
  const tableRows = useMemo(() => {
    const v = (r: Row): number => {
      switch (sort.k) {
        case 'sc': return r.sc ?? -1
        case 'vi': return r.vi
        case 'u': return r.u
        case 'conv': return r.conv ?? -1
        case 'ac': return r.ac ?? -1
        case 'ro': return r.ro ?? -1
        case 'np': return r.pdA.length
        default: return r.r || 0
      }
    }
    return [...rowsLista].sort((a, b) => (v(a) - v(b)) * sort.dir)
  }, [rowsLista, sort])
  useEffect(() => { setVisivel(PAGE_SIZE) }, [filtros, sort])
  const th = (k: SortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} onClick={() => setSort((s) => ({ k, dir: s.k === k ? (s.dir === 1 ? -1 : 1) : -1 }))} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label} <Box component="span" sx={{ opacity: 0.5, fontSize: 10 }}>{sort.k === k ? (sort.dir < 0 ? '▼' : '▲') : '⇅'}</Box>
    </TableCell>
  )

  const kpiClick = (k: keyof Filtros, v: Filtros[typeof k]) => ({ onClick: () => filtrar(k, v as never), selected: filtros[k] === v })
  const barClick = (k: 'faixa') => (d: unknown) => {
    const p = ((d as { payload?: { key?: string } }).payload ?? (d as { key?: string })) as { key?: string }
    if (!p.key) return
    if (p.key === 'sem') filtrar('nivel', 'sem'); else filtrar(k, p.key)
  }

  return (
    <DomainPageShell
      title="Score dos Anúncios"
      subtitle={meta?.generated_at
        ? `Extraído em ${new Date(meta.generated_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })} · ${nf(rowsAll.length)} anúncios ativos nas 2 contas · vendas e visitas de ${dia(w?.orders_from)} a ${dia(w?.orders_to)} (${diasJanela} dias)${meta.erros?.total ? ` · ⚠ coleta incompleta: ${nf(meta.erros.total)} chamadas à API falharam` : ''}`
        : 'Score do Mercado Livre + índice interno da Fiesta · dados da última coleta'}
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          {(['FESTA', 'HOME'] as const).filter((k) => meta?.[k]).map((k) => (
            <Typography key={k} variant="body2"><b>{NOME_CONTA[k]}</b> · {nf(rowsAll.filter((r) => r.acc === k).length)} anúncios · {meta?.[k]?.rep?.level_id || ''} {meta?.[k]?.rep?.power_seller_status || ''}</Typography>
          ))}
          {janelasOpt.length > 1 ? (
            <TextField select size="small" label="Período" value={janela} onChange={(e) => setJanela(e.target.value)} sx={{ minWidth: 160 }}>
              {janelasOpt.map((j) => <MenuItem key={j} value={String(j)}>Últimos {j} dias</MenuItem>)}
            </TextField>
          ) : null}
          <Typography variant="caption" color="text.secondary">Coleta automática às 05:30 (São Paulo) · publicidade {dia(w?.ads_from)} a {dia(w?.ads_to)}</Typography>
        </Stack>
        <Button variant="contained" disabled={coletando || loading} startIcon={coletando ? <CircularProgress size={14} color="inherit" /> : undefined} onClick={() => void dispararColeta()} sx={{ whiteSpace: 'nowrap' }}>
          {coletando ? 'Coletando…' : 'Atualizar agora'}
        </Button>
      </Stack>
      {coletaErro ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setColetaErro(null)}>{coletaErro}</Alert> : null}
      {coletaMsg ? <Alert severity="info" sx={{ mb: 2 }}>{coletaMsg}</Alert> : null}
      {meta?.erros?.total ? <AvisoColeta erros={meta.erros} /> : null}
      {!loading && rowsAll.length === 0 && !error ? <ChartEmpty title="Nenhum dado carregado." hint="Espera-se a última coleta em public/data/ml-score-anuncios.json ou no serviço de Score." suggestPeriod={false} /> : null}

      {rowsAll.length > 0 ? (
        <Card sx={{ mb: 2 }}>
          <Tabs value={aba} onChange={(_, v: Aba) => setAba(v)} variant="scrollable" allowScrollButtonsMobile sx={{ px: 1 }}>
            <Tab value="score" label="Score dos anúncios" />
            <Tab value="preco" label={`Preço × concorrência (${nf(rowsAll.filter((r) => r.pst).length)})`} />
          </Tabs>
        </Card>
      ) : null}

      {rowsAll.length > 0 && aba === 'preco' ? <AbaConcorrencia rowsAll={rowsAll} diasJanela={diasJanela} /> : null}

      {rowsAll.length > 0 && aba === 'score' ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Tudo neste painel mede a base inteira e é <b>clicável</b>: clique num número, numa barra ou num card para filtrar a lista "Todos os anúncios" lá embaixo. Os filtros ativos aparecem em chips junto da lista.
          </Typography>

          {/* KPIs — cada um filtra a lista */}
          <Grid container spacing={{ xs: 1.25, sm: 1.5 }} sx={{ mb: 2 }}>
            {[
              { label: 'Unidades por visita', value: visT ? `${nf((unT / visT) * 100, 2)}%` : '—', hint: `${nf(unT)} un. ÷ ${nf(visT)} visitas · clique: com venda`, ...kpiClick('soVenda', true) },
              { label: 'Anúncios ativos', value: nf(rowsAll.length), hint: nFiltros ? `${nf(rowsLista.length)} na lista · clique: limpar` : '2 contas, status active', onClick: limpar, selected: false },
              { label: 'Score médio ML', value: nf(scoreMedio, 1), hint: `${nf(comScore.length)} com score · clique: os ruins`, ...kpiClick('nivel', 'bad') },
              { label: 'Nível bom', value: pctFmt((nGood / rows.length) * 100, 0), hint: `${nf(nGood)} anúncios · clique: só bons`, ...kpiClick('nivel', 'good') },
              { label: `Receita ${diasJanela} dias`, value: fmtBrl(recT), hint: `${nf(sum(rows, (r) => r.o))} pedidos · clique: com venda`, ...kpiClick('soVenda', true) },
              { label: 'CTR de publicidade', value: prT ? pctFmt((clT / prT) * 100, 2) : '—', hint: `${nf(clT)} cliques / ${nf(prT)} impressões · clique: em campanha`, ...kpiClick('ads', 'active') },
              { label: 'Investimento em ads', value: fmtBrl(custoT), hint: `${diasJanela} dias, Product Ads · clique: em campanha`, ...kpiClick('ads', 'active') },
              { label: 'Nota média', value: avT ? nf(estrelas / avT, 2) : '—', hint: `${nf(avT)} avaliações · clique: notas baixas`, ...kpiClick('notaBaixa', true) },
              { label: 'Taxa de reclamação', value: pedT ? pctFmt((reclT / pedT) * 100, 2) : '—', hint: `${nf(reclT)} em ${nf(pedT)} pedidos · clique: com reclamação`, ...kpiClick('soReclamacao', true) },
              { label: 'Taxa de cancelamento', value: pedT ? pctFmt((cancT / pedT) * 100, 2) : '—', hint: `${nf(cancT)} cancelados · clique: com cancelamento`, ...kpiClick('soCancelamento', true) },
            ].map((k) => (
              <Grid key={k.label} size={{ xs: 6, sm: 4, md: 3, lg: 2.4 }}>
                <KpiCard label={k.label} value={k.value} hint={k.hint} loading={loading} onClick={k.onClick} selected={k.selected} />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mb: 2 }}>
            <PropostaIaCard
              title="Leitura da coleta"
              caption="Verdade = KPIs, fila e tabelas desta coleta. Proposta = leitura da IA — revise antes de agir."
              state={leituraIa}
              onRetry={() => loadAnalise()}
            />
          </Box>

          {/* Fila de ações — cada card com o botão que filtra a lista */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: 16 }} gutterBottom>O que fazer hoje</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Fila de trabalho por ordem de dinheiro em jogo. {FORA_DA_FILA}</Typography>
            <Grid container spacing={1.5}>
              {recs.map((g, i) => {
                const active = filtros.rec === g.id
                return (
                  <Grid key={g.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Card sx={{ height: '100%', outline: active ? '2px solid' : '1px solid', outlineColor: active ? 'primary.main' : g.p1 ? 'error.main' : 'divider', bgcolor: g.p1 ? alpha(theme.palette.error.main, 0.04) : 'background.paper' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{g.tit}</Typography>
                          <Chip size="small" label={`${i + 1}º`} variant="outlined" />
                        </Stack>
                        <Stack direction="row" spacing={1.5} sx={{ mb: 0.75 }}>
                          <Typography variant="body2"><b>{nf(g.n)}</b> anúncios</Typography>
                          <Typography variant="body2"><b>{fmtBrl(g.receita)}</b> em {diasJanela} dias</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>{g.acao}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.25 }}>Maiores: {g.top.map((r) => r.t.slice(0, 34)).join(' · ')}</Typography>
                        <Box sx={{ mt: 'auto' }}>
                          <Button size="small" variant={active ? 'contained' : 'outlined'} onClick={() => filtrar('rec', g.id)}>
                            {active ? 'Filtrando a lista ✓' : `Ver os ${nf(g.n)} anúncios`}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          </Box>

          {/* Gráficos — clicáveis */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Secao titulo="Distribuição do score" sub="Anúncios ativos por faixa de score do Mercado Livre (0–100). Clique numa barra.">
                <Box sx={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={distScoreData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={36} />
                      <ChartTip formatter={(value) => [Number(value), 'Anúncios']} contentStyle={{ borderRadius: 8, border: `1px solid ${theme.palette.divider}` }} />
                      <Bar dataKey="total" radius={[6, 6, 0, 0]} maxBarSize={48} cursor="pointer" onClick={barClick('faixa')}>
                        {distScoreData.map((d) => <Cell key={d.key} fill={(d.key === 'sem' ? filtros.nivel === 'sem' : filtros.faixa === d.key) ? theme.palette.primary.main : d.boa ? theme.palette.success.main : chart.line} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Secao>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Secao titulo="Conversão por faixa de score" sub="Unidades vendidas ÷ visitas, agregado por faixa. Clique numa barra.">
                <Box sx={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={convFaixaData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={(v) => `${Number(v).toFixed(1)}%`} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={42} />
                      <ChartTip formatter={(value, _n, item) => { const a = (item?.payload as { anuncios?: number } | undefined)?.anuncios; return [`${Number(value).toFixed(2)}%${a != null ? ` · ${a} anúncios` : ''}`, 'Conversão'] }} contentStyle={{ borderRadius: 8, border: `1px solid ${theme.palette.divider}` }} />
                      <Bar dataKey="conv" radius={[6, 6, 0, 0]} maxBarSize={48} cursor="pointer" onClick={barClick('faixa')}>
                        {convFaixaData.map((d) => <Cell key={d.key} fill={filtros.faixa === d.key ? theme.palette.primary.main : chart.pie[1] || chart.line} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Secao>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Secao titulo="Experiência de compra" sub={`Termômetro que o ML atribui a cada anúncio, e o que aconteceu nos pedidos dos últimos ${diasJanela} dias. Clique numa fatia.`}>
                <Stack direction="row" sx={{ height: 16, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                  {gzCounts.filter((g) => g.v > 0).map((g) => (
                    <Tooltip key={g.k} title={`${g.l}: ${nf(g.v)} — clique para filtrar`}>
                      <Box onClick={() => filtrar('gauge', g.k)} sx={{ width: `${(g.v / rows.length) * 100}%`, bgcolor: g.color, cursor: 'pointer', outline: filtros.gauge === g.k ? `2px solid ${theme.palette.primary.main}` : undefined, outlineOffset: -2 }} />
                    </Tooltip>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  {gzCounts.map((g) => (
                    <Chip key={g.k} size="small" variant={filtros.gauge === g.k ? 'filled' : 'outlined'} color={filtros.gauge === g.k ? 'primary' : 'default'} onClick={() => filtrar('gauge', g.k)}
                      icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color, ml: '6px !important' }} />} label={<>{g.l} · <b>{nf(g.v)}</b></>} />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Nos pedidos de {diasJanela} dias</Typography>
                {[
                  { l: 'Reclamações', n: reclT, cor: theme.palette.error.main, k: 'soReclamacao' as const },
                  { l: 'Cancelados', n: cancT, cor: theme.palette.warning.main, k: 'soCancelamento' as const },
                ].map((x) => (
                  <Barra key={x.l} label={x.l} valor={<><b>{nf(x.n)}</b> · {pedT ? pctFmt((x.n / pedT) * 100, 2) : '—'}</>} pct={pedT ? Math.min((x.n / pedT) * 100 * 12, 100) : 0} cor={x.cor} altura={6} ativo={filtros[x.k]} onClick={() => filtrar(x.k, true)} />
                ))}
                <Typography variant="caption" color="text.secondary">Ainda não entregues: <b>{nf(sum(rows, (r) => r.ne))}</b> · {pedT ? pctFmt((sum(rows, (r) => r.ne) / pedT) * 100, 2) : '—'}</Typography>
              </Secao>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Secao titulo="Avaliações dos compradores" sub="Estrelas somadas anúncio a anúncio. Clique numa linha para ver os anúncios com avaliação daquela nota.">
                {[4, 3, 2, 1, 0].map((i) => (
                  <Barra key={i} label={`${i + 1} ★`} valor={<><b>{nf(estrelasDist[i])}</b> · {estrelasTot ? pctFmt((estrelasDist[i] / estrelasTot) * 100, 1) : '—'}</>}
                    pct={(estrelasDist[i] / estrelasMax) * 100} cor={i >= 3 ? theme.palette.success.main : i === 2 ? theme.palette.warning.main : theme.palette.error.main}
                    ativo={filtros.estrelas === String(i + 1)} onClick={() => filtrar('estrelas', String(i + 1))} />
                ))}
                <Chip size="small" sx={{ mt: 0.5 }} variant={filtros.estrelas === '0' ? 'filled' : 'outlined'} color={filtros.estrelas === '0' ? 'primary' : 'default'} onClick={() => filtrar('estrelas', '0')}
                  label={`${nf(rows.filter((r) => r.nav === 0).length)} anúncios sem nenhuma avaliação`} />
                {rows.filter((r) => r.nav === null).length ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{nf(rows.filter((r) => r.nav === null).length)} não consultados (limite de requisições da API)</Typography> : null}
              </Secao>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Secao titulo="O que está travando o score" sub="Regras pendentes por número de anúncios, da maior alavanca para a menor. Clique numa regra.">
                {regrasPendentes.map((r) => (
                  <Barra key={r.k} label={r.pt} valor={<b>{nf(r.n)}</b>} pct={(r.n / regrasPendentes[0].n) * 100} cor={filtros.regra === r.k ? theme.palette.primary.main : chart.line} altura={9}
                    ativo={filtros.regra === r.k} onClick={() => filtrar('regra', r.k)} />
                ))}
              </Secao>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Secao titulo="Anúncios com baixa exposição" sub="Anúncio ativo que o ML está entregando menos do que poderia. Clique num motivo para filtrar a lista.">
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  {optExposicao.map((o) => (
                    <Chip key={o.k} size="small" color={filtros.exposicao === o.k ? 'primary' : 'warning'} variant={filtros.exposicao === o.k ? 'filled' : 'outlined'} onClick={() => filtrar('exposicao', o.k)} label={`${o.label} · ${nf(o.n)}`} title={EXPL[o.k]?.[1]} />
                  ))}
                </Stack>
                {baixaExpRows.length ? (
                  <Stack spacing={0.75}>
                    {baixaExpRows.map((r) => (
                      <Stack key={r.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ minWidth: 160, maxWidth: 260, fontSize: 12.5 }} noWrap title={r.t}>{r.t}</Typography>
                        <Chip size="small" variant="outlined" label={`${nf(r.vi)} visitas`} />
                        {r.exp.map((k) => <Chip key={k} size="small" color="warning" variant="outlined" label={EXPL[k]?.[0] || k} title={EXPL[k]?.[1]} onClick={() => filtrar('exposicao', k)} />)}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{fmtBrl(r.r)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : <ChartEmpty title="Nenhum anúncio com baixa exposição identificada." suggestPeriod={false} dense />}
              </Secao>
            </Grid>
          </Grid>

          {/* ====== A LISTA — filtros colados nela ====== */}
          <Card ref={tabelaRef} sx={{ scrollMarginTop: 16 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Typography variant="h6" sx={{ fontSize: 16, mr: 'auto' }}>Todos os anúncios</Typography>
                <Typography variant="caption" color="text.secondary">{nFiltros ? `${nf(rowsLista.length)} de ${nf(rowsAll.length)}` : `${nf(rowsAll.length)} anúncios`}</Typography>
                {nFiltros ? <Button size="small" variant="outlined" onClick={limpar}>Limpar tudo ({nFiltros})</Button> : null}
              </Stack>

              {/* Filtros em 4 blocos, na ordem da estrutura do dado: quem é o anúncio → o que o ML
                  diz dele → mídia → o que aconteceu / o que fazer. Pedido dela em 21/09/2026. */}
              {(() => {
                const sel = (label: string, k: keyof Filtros, itens: Array<[string, string]>, vazio: string) => (
                  <TextField select fullWidth size="small" label={label} value={String(filtros[k])} onChange={(e) => setF(k, e.target.value as never)}>
                    <MenuItem value="">{vazio}</MenuItem>
                    {itens.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                  </TextField>
                )
                return (
                  <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
                    <Bloco titulo="1 · Quem é o anúncio" md={3}>
                      <TextField size="small" fullWidth label="Buscar" placeholder="Título ou ID" value={filtros.busca} onChange={(e) => setF('busca', e.target.value)} />
                      {sel('Conta', 'conta', (['FESTA', 'HOME'] as const).filter((k) => meta?.[k]).map((k) => [k, `${NOME_CONTA[k]} (${nf(rowsAll.filter((r) => r.acc === k).length)})`]), 'Todas as contas')}
                      {sel('Logística', 'full', [['sim', `Só Full (${nf(rowsAll.filter((r) => r.fu).length)})`], ['nao', `Sem Full (${nf(rowsAll.filter((r) => !r.fu).length)})`]], 'Full e não-Full')}
                    </Bloco>
                    <Bloco titulo="2 · O que o ML diz dele" md={4}>
                      <Stack direction="row" spacing={1}>
                        {sel('Nível do score', 'nivel', ['good', 'medium', 'bad', 'sem'].map((k) => [k, `${NIVEL_LABEL[k]} (${nf(rowsAll.filter((r) => (k === 'sem' ? r.sc === null : r.lv === k)).length)})`]), 'Qualquer nível')}
                        {sel('Faixa de score', 'faixa', grupos.map((g) => [faixaKey(g.f), `${rotuloFaixa(g.f)} (${nf(g.rows.length)})`]), 'Qualquer faixa')}
                      </Stack>
                      {sel('Termômetro (experiência de compra)', 'gauge', ['healthy', 'warning', 'unhealthy', 'sem'].map((k) => [k, `${GAUGE_LABEL[k]} (${nf(rowsAll.filter((r) => (k === 'sem' ? !r.gz : r.gz === k)).length)})`]), 'Qualquer termômetro')}
                      {sel('Pendência do score', 'regra', optRegras.map((r) => [r.k, `${r.pt} (${nf(r.n)})`]), 'Qualquer pendência')}
                      {sel('Motivo de baixa exposição', 'exposicao', optExposicao.map((o) => [o.k, `${o.label} (${nf(o.n)})`]), 'Qualquer exposição')}
                    </Bloco>
                    <Bloco titulo="3 · Mídia" md={2}>
                      {sel('Publicidade', 'ads', optAds.map((o) => [o.k, `${o.label} (${nf(o.n)})`]), 'Qualquer status')}
                    </Bloco>
                    <Bloco titulo="4 · O que aconteceu e o que fazer" md={3}>
                      {sel('Ação da fila', 'rec', recs.map((g) => [g.id, `${g.tit} (${nf(g.n)})`]), 'Qualquer ação')}
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {([
                          ['soVenda', `Com venda ${diasJanela}d`], ['soReclamacao', 'Com reclamação'], ['soCancelamento', 'Com cancelamento'], ['notaBaixa', 'Nota ≤ 3,5'],
                        ] as Array<[keyof Filtros, string]>).map(([k, l]) => (
                          <Chip key={k} size="small" label={l} variant={filtros[k] ? 'filled' : 'outlined'} color={filtros[k] ? 'primary' : 'default'} onClick={() => setF(k, !filtros[k] as never)} />
                        ))}
                      </Stack>
                    </Bloco>
                  </Grid>
                )
              })()}

              {chipsFiltro.length ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>Filtros ativos:</Typography>
                  {chipsFiltro.map((c) => <Chip key={c.k} size="small" color="primary" label={c.label} onDelete={() => setF(c.k, FILTROS_VAZIOS[c.k] as never)} />)}
                </Stack>
              ) : null}

              {tableRows.length === 0 ? (
                <ChartEmpty title="Nenhum anúncio com esses filtros." hint="Afrouxe ou limpe os filtros para voltar à base completa." suggestPeriod={false} dense />
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{nf(tableRows.length)} anúncios · clique no cabeçalho para ordenar</Typography>
                  <Box sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table size="small" sx={{ minWidth: 1060, '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', bgcolor: 'action.hover' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Anúncio</TableCell>
                          <TableCell>Conta</TableCell>
                          {th('sc', 'Score')}
                          <TableCell>Termômetro</TableCell>
                          {th('vi', `Visitas ${diasJanela}d`)}
                          {th('u', `Un. ${diasJanela}d`)}
                          {th('conv', 'Un./visita')}
                          {th('r', `Receita ${diasJanela}d`)}
                          <TableCell align="right">CTR</TableCell>
                          {th('ac', 'ACOS')}
                          {th('ro', 'ROAS')}
                          <TableCell>Preço</TableCell>
                          {th('np', 'Pendências', 'left')}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableRows.slice(0, visivel).map((r) => {
                          const pst = r.pst ? PST_LABEL[r.pst] : null
                          const tags = r.pdA.map((k) => RULE_PT[k] || k)
                          const gzCor = r.gz === 'healthy' ? 'success.main' : r.gz === 'warning' ? 'warning.main' : r.gz === 'unhealthy' ? 'error.main' : 'text.disabled'
                          return (
                            <TableRow key={r.id} hover>
                              <TableCell sx={{ maxWidth: 300 }}>
                                {r.lk ? <Link href={r.lk} target="_blank" rel="noreferrer" underline="hover" noWrap sx={{ display: 'block', fontWeight: 600 }}>{r.t}</Link> : <Typography variant="body2" fontWeight={600} noWrap>{r.t}</Typography>}
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">{r.id}</Typography>
                                  {r.exp.map((k) => <Chip key={k} size="small" color="warning" variant="outlined" label={EXPL[k]?.[0] || k} title={EXPL[k]?.[1]} sx={{ height: 18, fontSize: 10 }} onClick={() => filtrar('exposicao', k)} />)}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.acc === 'FESTA' ? 'Festa' : r.acc === 'HOME' ? 'Home' : r.acc}</TableCell>
                              <TableCell align="right">
                                {r.sc == null ? <Typography variant="caption" color="text.secondary">sem score</Typography>
                                  : <Chip size="small" label={r.sc} color={r.lv === 'good' ? 'success' : r.lv === 'medium' ? 'warning' : 'error'} variant="outlined" onClick={() => filtrar('nivel', r.lv || '')} />}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => filtrar('gauge', r.gz || 'sem')}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: gzCor }} />
                                  <Typography variant="caption">{GAUGE_LABEL[r.gz || 'sem']?.split(' (')[0]}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="right">{nf(r.vi)}</TableCell>
                              <TableCell align="right">{nf(r.u)}</TableCell>
                              <TableCell align="right">{r.conv == null ? '—' : pctFmt(r.conv, 2)}</TableCell>
                              <TableCell align="right">{r.r ? fmtBrl(r.r) : '—'}</TableCell>
                              <TableCell align="right">{r.ct == null ? '—' : pctFmt(r.ct, 2)}</TableCell>
                              <TableCell align="right">{r.ac == null ? '—' : pctFmt(r.ac, 1)}</TableCell>
                              <TableCell align="right" title={r.ro == null ? 'fora do Product Ads na janela' : 'receita do Ads ÷ custo do Ads'}>{r.ro == null ? '—' : `${nf(r.ro, 2)}x`}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {r.p != null ? fmtBrl(r.p) : '—'}
                                {pst ? <Chip size="small" label={pst[1]} sx={{ ml: 0.5, height: 18, fontSize: 10, color: pst[0], borderColor: pst[0] }} variant="outlined" /> : null}
                              </TableCell>
                              <TableCell sx={{ minWidth: 220 }}>
                                {tags.length ? (
                                  <Tooltip title={<Box sx={{ fontSize: 12 }}>{tags.map((t) => <div key={t}>• {t}</div>)}</Box>} arrow>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                      {r.pdA.slice(0, 3).map((k) => <Chip key={k} size="small" variant="outlined" label={RULE_PT[k] || k} sx={{ height: 20, fontSize: 10.5 }} onClick={() => filtrar('regra', k)} />)}
                                      {tags.length > 3 ? <Chip size="small" label={`+${tags.length - 3}`} sx={{ height: 20, fontSize: 10.5 }} /> : null}
                                    </Stack>
                                  </Tooltip>
                                ) : <Typography variant="caption" color="success.main">sem pendência</Typography>}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                  {visivel < tableRows.length ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                      <Button variant="outlined" onClick={() => setVisivel((v) => v + PAGE_SIZE)}>Carregar mais ({nf(tableRows.length - visivel)} restantes)</Button>
                    </Box>
                  ) : null}
                </>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Publicidade: {Object.entries(ADS_LABEL).map(([k, v]) => `${k} = ${v}`).join(' · ')}. Preço × concorrência:
                aba acima nesta mesma tela (comparação do ML).
              </Typography>
            </CardContent>
          </Card>
        </>
      ) : null}
    </DomainPageShell>
  )
}
